import { useQuery } from '@apollo/client';
import { Divider } from '@chakra-ui/react';
import * as React from 'react';

import {
  AdminPage,
  AdminPageContent,
  AdminPageHeader,
} from '@/components/AdminPage';
import { useCurrent } from '@/hooks/useCurrent';
import { AdminTopBarNavMenuFrame } from '@/imports/ui/admin/layouts/AdminTopBarNavMenuFrame';
import IntegrationAccountManagement from '@/imports/ui/admin/pages/Integration/IntegrationAccountManagement';
import { useFullSyncStatus } from '@/imports/ui/admin/pages/Integration/hooks/useFullSyncStatus';
import { GET_ALL_AMS_INFO } from '@/imports/ui/admin/pages/Integration/query';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { AmsControlledAttributeSchemasPicker } from '@/imports/ui/pages/morgoth/IntegrationAccountManagementPage/components';
import { RouteRestrictions } from '@/utilities';
import { restrictedRoute } from '@/utilities/restrictedRoute';

interface GetAllAmsInfo_getAllAmsInfo_results {
  _id: string;
  name: string;
  type: string;
  description: string;
  amsTenantInfo: {
    isActive: boolean;
  };
}

interface GetAllAmsInfo_getAllAmsInfo {
  totalCount: number;
  results: GetAllAmsInfo_getAllAmsInfo_results[];
}

interface GetAllAmsInfo {
  getAllAmsInfo: GetAllAmsInfo_getAllAmsInfo;
}

const IntegrationAccountManagementPage: React.FC = () => {
  const current = useCurrent();
  const tenantId = current.tenant?._id || '';

  const { data: amsData, loading: amsLoading } = useQuery<GetAllAmsInfo>(
    GET_ALL_AMS_INFO,
    {
      variables: {
        searchText: null,
      },
      fetchPolicy: 'network-only',
    }
  );

  // Find the active AMS
  const activeAms = React.useMemo(() => {
    if (!amsData?.getAllAmsInfo?.results) return '';
    const active = amsData.getAllAmsInfo.results.find(
      (item) => item.amsTenantInfo.isActive
    );
    return active?._id || '';
  }, [amsData]);

  const { fullSyncStatusData, refetchFullSyncStatus } = useFullSyncStatus(
    activeAms,
    tenantId
  );

  if (amsLoading || !tenantId) {
    return (
      <AdminTopBarNavMenuFrame>
        <Loading />
      </AdminTopBarNavMenuFrame>
    );
  }

  return (
    <AdminTopBarNavMenuFrame>
      <AdminPage maxWidth="full" mx="auto">
        <AdminPageHeader mb="4" fontSize="20px" fontWeight="bold">
          Integration Account Management
        </AdminPageHeader>
        <AdminPageContent>
          <IntegrationAccountManagement
            fullSyncStatusData={fullSyncStatusData || {}}
            refetchFullSyncStatus={refetchFullSyncStatus}
            activeAms={activeAms}
            tenantId={tenantId}
          />
          <Divider my={6} />
          <AmsControlledAttributeSchemasPicker />
        </AdminPageContent>
      </AdminPage>
    </AdminTopBarNavMenuFrame>
  );
};

export default restrictedRoute(IntegrationAccountManagementPage, {
  requiresEvery: [RouteRestrictions.ADMIN_ROLE],
});
